import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "../contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import toast from "react-hot-toast";
import MoreActionsMenu from "../components/common/MoreActionsMenu";
import aboutImage1 from "../assets/about-page-images/about-image-1.png";
import aboutImage2 from "../assets/about-page-images/about-image-2.png";
import mainImage from "../assets/about-page-images/main.jpg";
import aboutBanner from "../assets/about-page-images/about-banner.png";

const Container = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 3rem 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: fixed;
    top: 10%;
    left: -200px;
    width: 400px;
    height: 400px;
    background: url(${aboutImage1}) no-repeat center center;
    background-size: cover;
    opacity: 0.15;
    z-index: -1;
    transform: rotate(-15deg);
    mask: radial-gradient(ellipse at center, black 60%, transparent 85%);
    -webkit-mask: radial-gradient(
      ellipse at center,
      black 60%,
      transparent 85%
    );
  }

  &::after {
    content: "";
    position: fixed;
    bottom: 15%;
    right: -175px;
    width: 350px;
    height: 350px;
    background: url(${aboutImage2}) no-repeat center center;
    background-size: cover;
    opacity: 0.2;
    z-index: -1;
    transform: rotate(12deg);
    mask: radial-gradient(ellipse at center, black 60%, transparent 85%);
    -webkit-mask: radial-gradient(
      ellipse at center,
      black 60%,
      transparent 85%
    );
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subheading = styled.p`
  text-align: center;
  font-family: var(--font-subheading);
  color: var(--color-text-light);
  font-size: 1.125rem;
  margin-bottom: 3rem;
  font-style: italic;
`;

const Content = styled.div`
  font-size: 1.125rem;
  line-height: 1.8;

  p {
    margin-bottom: 2rem;
  }

  h2 {
    margin-top: 3rem;
    margin-bottom: 1.5rem;
    font-size: 1.75rem;
  }

  h3 {
    margin-top: 2rem;
    margin-bottom: 1rem;
    font-size: 1.375rem;
  }

  blockquote {
    margin: 3rem 0 4rem 0;
    text-align: center;

    p {
      font-size: 1.5rem;
      margin-bottom: 1rem;
      font-style: italic;

      &:last-child {
        font-size: 1rem;
        font-style: normal;
        color: var(--color-text-light);
      }
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin-bottom: 2rem;

    li {
      margin-bottom: 1rem;
      padding-left: 1.5rem;
      position: relative;

      &:before {
        content: "•";
        position: absolute;
        left: 0;
        color: var(--color-accent);
      }

      em {
        font-family: var(--font-body);
        font-style: italic;
      }
    }
  }

  > p:last-of-type {
    text-align: center;
    font-size: 1.125rem;
    margin-top: 3rem;
  }
`;

const MainImageContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto 3rem;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const MainImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  margin-bottom: 2rem;
  position: relative;
`;

const TitleSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const AboutBanner = styled.img`
  display: block;
  margin: 0 auto 1.5rem;
  width: 45%;
  max-width: 300px;
  height: auto;
`;

const EditContainer = styled.div`
  background: var(--color-bg);
  border-radius: 12px;
  overflow: hidden;
  margin: 2rem 0;
`;

const EditInput = styled.input`
  width: 100%;
  padding: 1rem 2rem;
  border: none;
  outline: none;
  font-size: 1.125rem;
  font-family: var(--font-subheading);
  line-height: 1.4;
  background: var(--color-white);
  border-bottom: 1px solid var(--color-border);
  font-style: italic;
  color: var(--color-text-light);

  &:focus {
    box-shadow: inset 0 0 0 2px var(--color-accent);
  }

  &::placeholder {
    color: var(--color-text-light);
  }
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 600px;
  padding: 2rem;
  border: none;
  outline: none;
  font-size: 1rem;
  font-family: var(--font-mono, monospace);
  line-height: 1.6;
  resize: vertical;
  background: var(--color-white);

  &:focus {
    box-shadow: inset 0 0 0 2px var(--color-accent);
  }
`;

const EditFooter = styled.div`
  padding: 1rem 2rem;
  border-top: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-white);
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;

  &.save {
    background: var(--color-accent);
    color: white;

    &:hover:not(:disabled) {
      background: var(--color-accent-hover);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.cancel {
    color: var(--color-text);
    border: 1px solid var(--color-border);

    &:hover {
      background: var(--color-bg);
    }
  }
`;

const About = () => {
  const defaultContent = `> "The only thing I know is that I know nothing."
> — Socrates

Hello! 👋 I'm the writer for this blog. I go by Isabel, Jade, Isabel Jade, and any iteration in between.

This blog is a time log of my pursuit of meaning in a world of total abstraction. The writing here ranges from essays to brief musings about something completely random, or even poetry 📝 (though I admit I'm very selective about the poetry I post — it teeters precariously between the beautiful and the revoltingly cliché).

After two to three years of writing all my miscellaneous thoughts in my Notes app, I realized I didn't want to leave my writing to rot in that digital abyss. There's something profoundly human about sharing ideas, watching them take root in other minds.

I have always loved showing my short essays to friends. There is nothing quite like seeing the spark of understanding ✨ illuminate their faces when they grasp why I am so passionate about a topic. What's even more exhilarating is witnessing my friends weave these threads of inquiry into their daily lives, entangling seemingly disparate concepts into more abstract perspectives.

I spent months writing essays for competitions, articles for school magazines — all in a desperate pursuit to have my voice heard. At one point, it dawned upon me: why constrain my writing to the boundaries of educational institutions?

Well, I can write freely. And that I did. Hence, the conception of jisdeu.com.

## Why jisdeu?

Despite the tagline 'in pursuit of meaning', the name of my website ironically carries little meaning itself. My only criterion was that I wanted the letters 'i' and 'j' in the brand name. The 'deu' was somewhat inspired by my trip to Paris 🇫🇷, where I found myself enthralled by the musicality of the French language. Essentially, jisdeu was conceived purely for aesthetics.

I enjoy when people ask about the website's name. Keep pursuing meaning in everything. Continue attempting to justify every creation in the world. Sometimes that pursuit may be futile, but I believe we are born to chase it anyway.

## Who are you?

I am a Singaporean 🇸🇬 currently studying A Levels in the UK 🇬🇧. I am nothing more than an average student navigating the world with the typical air of teenage angst and wonder.

I devour philosophical fiction, philosophy, anthropology, and just about anything that illuminates the human condition. As a creative, I write (shocker!), draw 🎨, paint, crochet 🧶, make pottery, play the guitar 🎸 (poorly), and pursue a constellation of other artistic endeavours.

### Books that have changed my life 📚
(in no particular order except for the first title):

- *Man's Search for Meaning* by Viktor Frankl
- *Essays in Love* by Alain de Botton
- *What Does It All Mean?* by Thomas Nagel
- *The Outsider* by Albert Camus

I hope this glimpse behind the curtain has given this website more personality. Welcome to my corner of the internet 🌐, where questions matter more than answers, and the pursuit itself is the meaning.

Thanks for reading ❤️`;

  const [content, setContent] = useState("");
  const [subheading, setSubheading] = useState(
    "In pursuit of meaning through words and wonder"
  );
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editSubheading, setEditSubheading] = useState("");
  const [saving, setSaving] = useState(false);
  const { currentUser, isAuthorized } = useAuth();
  const textareaRef = useRef(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const docRef = doc(db, "pages", "about");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setContent(data.content);
          setSubheading(
            data.subheading || "In pursuit of meaning through words and wonder"
          );
        } else {
          // Create the document with default content if it doesn't exist
          await setDoc(docRef, {
            content: defaultContent,
            subheading: "In pursuit of meaning through words and wonder",
            updatedAt: new Date(),
          });
          setContent(defaultContent);
        }
      } catch (error) {
        console.error("Error fetching about content:", error);
        toast.error("Failed to load content");
        setContent(defaultContent);
      } finally {
        setLoading(false);
      }
    };

    // Set up real-time listener
    const unsubscribe = onSnapshot(doc(db, "pages", "about"), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setContent(data.content);
        setSubheading(
          data.subheading || "In pursuit of meaning through words and wonder"
        );
      }
    });

    fetchContent();

    return () => unsubscribe();
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setEditContent(content);
    setEditSubheading(subheading);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(0, 0);
      }
    }, 100);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "pages", "about"), {
        content: editContent,
        subheading: editSubheading,
        updatedAt: new Date(),
      });
      setIsEditing(false);
      toast.success("About page updated!");
    } catch (error) {
      console.error("Error saving content:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditContent("");
    setEditSubheading("");
  };

  if (loading) {
    return (
      <Container>
        <TitleSection>
          <AboutBanner src={aboutBanner} alt="About — in pursuit of meaning" />
        </TitleSection>
        <Content>Loading...</Content>
      </Container>
    );
  }

  return (
    <Container>
      <TitleSection>
        <AboutBanner src={aboutBanner} alt="About — in pursuit of meaning" />
      </TitleSection>

      {isAuthorized && (
        <Header>
          <MoreActionsMenu
            item={{ id: "about" }}
            collection="pages"
            onEdit={handleEdit}
            showAlways={true}
          />
        </Header>
      )}

      {!isEditing && (
        <>
          <MainImageContainer>
            <MainImage src={mainImage} alt="About page main image" />
          </MainImageContainer>

          <Content>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </Content>
        </>
      )}

      {isEditing && (
        <EditContainer>
          <EditInput
            value={editSubheading}
            onChange={(e) => setEditSubheading(e.target.value)}
            placeholder="Page subheading..."
          />
          <EditTextarea
            ref={textareaRef}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            placeholder="Write your about page content in markdown..."
          />
          <EditFooter>
            <div>
              <small style={{ color: "var(--color-text-light)" }}>
                Markdown supported
              </small>
            </div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <EditButton className="cancel" onClick={handleCancel}>
                Cancel
              </EditButton>
              <EditButton
                className="save"
                onClick={handleSave}
                disabled={
                  saving || (!editContent.trim() && !editSubheading.trim())
                }
              >
                {saving ? "Saving..." : "Save"}
              </EditButton>
            </div>
          </EditFooter>
        </EditContainer>
      )}
    </Container>
  );
};

export default About;
